import { useEffect, useMemo, useState } from "react";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import type { NormalGatepassFormData } from "@features/gatepass/schema/normalGatepassFormSchema";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { REFERENCE_DATA_TYPES, registrationEmirates } from "@services/referenceDataApi";
import { t } from "i18next";
import { FormProvider, type UseFormReturn } from "react-hook-form";
import { useOutletContext } from "react-router-dom";

export interface FormContext {
  formData: UseFormReturn<NormalGatepassFormData>;
  updateFormData: (key: string, value: unknown) => void;
}

type VehicleType = {
  [key: string]: string;
};

const TRANSLATION_KEY = "gatepass";

const VehicleDetails = () => {
  const { formData, updateFormData } = useOutletContext<FormContext>();
  const { options: vehicleTypes } = useFormattedReference<VehicleType>(
    REFERENCE_DATA_TYPES.VEHICLE_TYPES,
    "bff-gatepass",
  );

  const vehicleType = formData.watch("vehicleType");
  const [showVehicleFields, setShowVehicleFields] = useState(false);

  const formattedVehicleTypes = useMemo(
    () => [
      { value: "", label: t("gatepass.no-vehicle") },
      ...vehicleTypes.map((vehicleType) => ({
        value: vehicleType[Object.keys(vehicleType)[0]],
        label: vehicleType[Object.keys(vehicleType)[0]],
      })),
    ],
    [vehicleTypes],
  );

  // Category options - these should be defined based on requirements
  const categoryOptions = useMemo(
    () => [
      { value: "S", label: "Small" },
      { value: "M", label: "Medium" },
      { value: "L", label: "Large" },
    ],
    [],
  );

  // Show vehicle fields if a vehicle type is selected
  useEffect(() => {
    setShowVehicleFields(!!vehicleType && vehicleType !== "");
  }, [vehicleType]);

  return (
    <FormProvider {...formData}>
      <FormSection title={t(`${TRANSLATION_KEY}.vehicle-details`)}>
        <div className="grid col-span-2 grid-cols-4 gap-4">
          <FormItem<NormalGatepassFormData, "vehicleType">
            name="vehicleType"
            type="autocomplete"
            label={t(`${TRANSLATION_KEY}.vehicle-type`)}
            options={formattedVehicleTypes}
            testId="normalGatepass-vehicleType"
            onFieldChange={updateFormData as unknown as (key: string, value: unknown) => void}
          />
          {showVehicleFields && (
            <>
              <FormItem<NormalGatepassFormData, "registrationEmirate">
                required
                name="registrationEmirate"
                type="autocomplete"
                options={registrationEmirates}
                label={t(`${TRANSLATION_KEY}.registration-emirate`)}
                testId="normalGatepass-registrationEmirate"
                onFieldChange={updateFormData as unknown as (key: string, value: unknown) => void}
              />
              <FormItem<NormalGatepassFormData, "vehicleColour">
                name="vehicleColour"
                type="text"
                label={t(`${TRANSLATION_KEY}.vehicle-colour`)}
                testId="normalGatepass-vehicleColour"
                onFieldChange={updateFormData as unknown as (key: string, value: unknown) => void}
              />
              <FormItem<NormalGatepassFormData, "category">
                name="category"
                type="autocomplete"
                options={categoryOptions}
                label={t(`${TRANSLATION_KEY}.category`)}
                testId="normalGatepass-category"
                onFieldChange={updateFormData as unknown as (key: string, value: unknown) => void}
              />
              <FormItem<NormalGatepassFormData, "plateNumber">
                required
                name="plateNumber"
                type="text"
                label={t(`${TRANSLATION_KEY}.vehicle-plate-number`)}
                testId="normalGatepass-plateNumber"
                onFieldChange={updateFormData as unknown as (key: string, value: unknown) => void}
              />
            </>
          )}
        </div>
      </FormSection>
    </FormProvider>
  );
};

export default VehicleDetails;
